import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import CircleLoader from '~/editor/loader/CircleLoader';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Button } from '~/ui/buttons';

const Container = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  perspective: 1000px;
  z-index: 500;
  background: rgba(0, 0, 0, 0.69);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  min-height: 410px;
`;

const Popup = styled.div`
  width: 480px;
  position: relative;
  z-index: 9999;
  line-height: 1.5;

  &:after {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: linear-gradient(
      139deg,
      #9738ff 19.33%,
      #3870ff 49%,
      #00e8f9 73.51%,
      #6df900 100.36%
    );
    z-index: -1;
    filter: blur(20px);
    opacity: 0.8;
  }

  .closeBtn {
    margin-left: calc(100% - 61px);
    margin-top: -10px;
  }
`;

const PopupWrapper = styled.div`
  border-radius: 16px;
  padding: 20px 40px;
  background-color: #fff;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h3`
  font-family: ralewayBold;
  letter-spacing: -0.5px;
  margin-bottom: 9px;
  font-size: 17px;
  font-weight: 700;
  color: #2e2e2e;
`;

const Description = styled.p`
  margin-bottom: 11px;
  font-size: 12px;
  color: #6f6f6f;
`;

const LoadingMessage = styled.p`
  margin-top: 12px;
  font-size: 11px;
  color: #6f6f6f;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 140px;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  line-height: 1.5;
  color: #2e2e2e;
  resize: none;
  outline: none;
  margin-bottom: 12px;
  box-sizing: border-box;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: left;
  gap: 12px;
`;

const SubmitButton = styled(Button)`
  font-family: Raleway;
  font-weight: 700;
  font-size: 14px;
  padding: 11px 20px;
`;

interface PromptPopupProps {
  onClose: () => void;
}

const IN_PROGRESS_TEXT = 'Generating';

const AIPromptPopup: React.FC<PromptPopupProps> = ({ onClose }) => {
  const {
    modifySequenceWithAI,
    sequence,
    aiModification,
    setAIModificationStatus,
    resetAIModificationStatus
  } = useEditorStore();
  const { isLoading, error, success, lastPrompt } = aiModification;
  const [prompt, setPrompt] = useState<string>(lastPrompt || '');
  const [progressText, setProgressText] = useState<string>(IN_PROGRESS_TEXT);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      resetAIModificationStatus();
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isLoading) {
      interval = setInterval(() => {
        setProgressText(prev => {
          if (prev.endsWith('...')) {
            return prev.slice(0, -3);
          }
          return prev + '.';
        });
      }, 500);
    } else {
      setProgressText(IN_PROGRESS_TEXT);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoading]);

  useEffect(() => {
    if (success) {
      onClose();
    }
  }, [success, onClose]);

  useEffect(() => {
    if (!isLoading && error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: error
      });
    }
  }, [isLoading, error]);

  const handleRegenerate = async () => {
    if (!prompt.trim()) return;

    if (!sequence?.id) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'No sequence selected for modification.'
      });
      return;
    }

    await modifySequenceWithAI(prompt);
  };

  return (
    <Container>
      <Popup>
        <PopupWrapper>
          <CloseButton type="close" onClick={onClose} autoFocus={false} />
          <Title>Want to switch it up?</Title>
          <Description>
            Generate brand-new content or rewrite with a different tone and
            style. We'll keep the layout, narrations, and images as is.
          </Description>
          <TextArea
            value={prompt}
            onChange={e => {
              const newValue = e.target.value;
              setPrompt(newValue);

              setAIModificationStatus({ lastPrompt: newValue });
            }}
            placeholder="Type your new idea or rewrite here"
            onFocus={e => e.target.select()}
          />
          <ButtonGroup>
            <SubmitButton
              onClick={handleRegenerate}
              disabled={!prompt.trim() || isLoading}
            >
              {isLoading ? progressText : 'Re-Generate Text'}
            </SubmitButton>
          </ButtonGroup>
          {isLoading && (
            <LoadingMessage>
              Hang tight — your content is being generated. This may take up to
              10 seconds.
            </LoadingMessage>
          )}
        </PopupWrapper>
      </Popup>
    </Container>
  );
};

export default AIPromptPopup;
